import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api, type QueueItem } from "../lib/api";

/** The specialist floor view.
 *
 *  Specialists reply inside Five9's own chat, so this is deliberately not an
 *  inbox. It is the queue plus enough context to pick up a conversation, and
 *  a link that opens the right person in Five9.
 */
export function Queue() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<QueueItem | null>(null);

  const load = () =>
    api
      .queue()
      .then((r) => {
        setItems(r.items);
        setSelected((s) => s ?? r.items[0] ?? null);
        setError(null);
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="sidebar">
      <div className="col">
        <ErrorBanner error={error} />
        <div className="row w">
          <div>
            <h2 style={{ fontSize: 19 }}>Waiting on you</h2>
            <span className="lab">people who replied in the last 7 days</span>
          </div>
          <div className="sp" />
          <Pill tone="ac">{items.length} waiting</Pill>
        </div>

        {items.length === 0 ? (
          <Card>
            <div className="empty">
              Nobody is waiting. Replies appear here within seconds of arriving.
            </div>
          </Card>
        ) : (
          items.map((q) => (
            <div
              key={q.contact_id}
              className="card"
              style={{
                padding: "11px 14px",
                cursor: "pointer",
                borderColor:
                  selected?.contact_id === q.contact_id ? "var(--acc-line)" : undefined,
                background:
                  selected?.contact_id === q.contact_id ? "var(--acc-soft)" : undefined,
              }}
              onClick={() => setSelected(q)}
            >
              <div className="row w" style={{ gap: 8 }}>
                <b style={{ fontSize: 13 }}>{q.name}</b>
                {q.classification && <Pill tone="ac">{q.classification.replace(/_/g, " ")}</Pill>}
                <span className="dim mono" style={{ fontSize: 10.5 }}>
                  {fmt.when(q.last_reply_at)}
                </span>
                <div className="sp" />
                <span className="mono dim" style={{ fontSize: 11 }}>
                  {q.phone}
                </span>
              </div>
              {q.body && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: "var(--ink2)",
                    marginTop: 4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {q.body}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="col">
        {selected ? (
          <>
            <Card title={selected.name}>
              <div className="col" style={{ gap: 11 }}>
                {/* Five9 is where the conversation actually happens. Until
                    they confirm a deep-link format this opens their app; the
                    context is what makes it useful either way. */}
                <a
                  className="btn p"
                  style={{ justifyContent: "center" }}
                  href="https://login.five9.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Five9
                </a>

                {[
                  ["Phone", selected.phone],
                  ["State", selected.state ?? "—"],
                  ["Trips taken", String(selected.flight_count)],
                  ["Score", selected.lead_score != null ? String(selected.lead_score) : "not scored"],
                  ["Replied", fmt.when(selected.last_reply_at)],
                ].map(([k, v]) => (
                  <div className="row" key={k} style={{ fontSize: 12.5 }}>
                    <span className="dim" style={{ width: 92 }}>
                      {k}
                    </span>
                    <span className="mono">{v}</span>
                  </div>
                ))}

                {selected.body && (
                  <div>
                    <span className="lab">What they said</span>
                    <div className="bub in">{selected.body}</div>
                  </div>
                )}

                <Link className="btn" to={`/contacts/${selected.contact_id}`} style={{ justifyContent: "center" }}>
                  See everything about them
                </Link>
              </div>
            </Card>

            <Card title="What happens next">
              <p className="mut" style={{ fontSize: 12.5 }}>
                Replying is done in Five9. When a call connects or a sale is recorded, this
                person moves to the next campaign on their own — nobody needs to tag them.
              </p>
            </Card>
          </>
        ) : (
          <Card>
            <div className="empty">Pick someone from the list.</div>
          </Card>
        )}
      </div>
    </div>
  );
}
